// Client-side social/SEO metadata for the public Customer Booking Page.
//
// REALITY CHECK (platform limitation, confirmed): production serves a
// static build for /l/{ownerId}, so non-JS social crawlers (Facebook,
// WhatsApp, iMessage) always see the Tasklio-branded fallback tags baked
// into public/index.html. These dynamic updates benefit JS-executing
// crawlers (Googlebot), browser tabs, bookmarks, and any platform that
// renders the page. Metadata is intentionally English-only so crawlers get
// stable values regardless of the visitor-selected page language.
const SELECTORS = {
  description: 'meta[name="description"]',
  ogTitle: 'meta[property="og:title"]',
  ogDescription: 'meta[property="og:description"]',
  ogUrl: 'meta[property="og:url"]',
  ogImage: 'meta[property="og:image"]',
  ogImageSecure: 'meta[property="og:image:secure_url"]',
  ogImageWidth: 'meta[property="og:image:width"]',
  ogImageHeight: 'meta[property="og:image:height"]',
  ogImageAlt: 'meta[property="og:image:alt"]',
  twTitle: 'meta[name="twitter:title"]',
  twDescription: 'meta[name="twitter:description"]',
  twImage: 'meta[name="twitter:image"]',
  twImageAlt: 'meta[name="twitter:image:alt"]',
};

const setContent = (selector, value) => {
  const el = document.querySelector(selector);
  if (el && value != null) el.setAttribute("content", value);
};

// Applies booking-page metadata and returns a restore function that puts
// the original Tasklio landing-page metadata back (used on unmount so
// in-SPA navigation to other routes keeps the global tags intact).
export const applyBookingPageMeta = ({ business, ownerId, backendUrl }) => {
  const name = (business?.business_name || "").trim();
  if (!name) return () => {};

  const originalTitle = document.title;
  const originals = {};
  Object.entries(SELECTORS).forEach(([key, sel]) => {
    const el = document.querySelector(sel);
    originals[key] = el ? el.getAttribute("content") : null;
  });
  const existingCanonical = document.querySelector('link[rel="canonical"]');

  const title = `${name} | Book a Cleaning`;
  const tagline = (business.booking_tagline || "").trim();
  const description = tagline || `Request a cleaning quote from ${name}.`;
  const pageUrl = `${window.location.origin}/l/${ownerId}`;
  const logoUrl = (business.booking_logo_url || "").trim();
  const image = logoUrl
    ? `${backendUrl}${logoUrl}`
    : `${window.location.origin}/og-image.png`;
  const imageAlt = logoUrl
    ? `${name} logo`
    : "Tasklio AI — Instant quotes for cleaning businesses";
  // Logos are optimized server-side to fit within 512px; the fallback card
  // is the known 1200x630 asset.
  const imgW = logoUrl ? "512" : "1200";
  const imgH = logoUrl ? "512" : "630";

  document.title = title;
  setContent(SELECTORS.description, description);
  setContent(SELECTORS.ogTitle, title);
  setContent(SELECTORS.ogDescription, description);
  setContent(SELECTORS.ogUrl, pageUrl);
  setContent(SELECTORS.ogImage, image);
  setContent(SELECTORS.ogImageSecure, image);
  setContent(SELECTORS.ogImageWidth, imgW);
  setContent(SELECTORS.ogImageHeight, imgH);
  setContent(SELECTORS.ogImageAlt, imageAlt);
  setContent(SELECTORS.twTitle, title);
  setContent(SELECTORS.twDescription, description);
  setContent(SELECTORS.twImage, image);
  setContent(SELECTORS.twImageAlt, imageAlt);

  let canonical = existingCanonical;
  let createdCanonical = false;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
    createdCanonical = true;
  }
  const originalCanonicalHref = createdCanonical ? null : canonical.getAttribute("href");
  canonical.setAttribute("href", pageUrl);

  return () => {
    document.title = originalTitle;
    Object.entries(SELECTORS).forEach(([key, sel]) => {
      if (originals[key] != null) setContent(sel, originals[key]);
    });
    if (createdCanonical) {
      canonical.remove();
    } else if (originalCanonicalHref != null) {
      canonical.setAttribute("href", originalCanonicalHref);
    }
  };
};
